/*
 * CS 21.40 — Retailer Link Shopfront (polish pass).
 *
 * Product decisions locked in this turn:
 *   1. Wholesaler name as the brand (CS 22.11: no Ashth branding on /wr links).  Prominent central
 *      search under it.
 *   2. Grid: 1 col mobile / 2 tablet / 3 desktop.
 *   3. Cards: subtle image frame, top-left checkbox (always visible,
 *      matches the wholesaler grid).
 *   4. Prices ON the grid — permitted tiers, driven by a per-link toggle
 *      the retailer flips.  The toggle can only expand to tiers the
 *      wholesaler enabled.  A wholesaler-hidden tier has NO toggle at all;
 *      the payload key never even arrives.
 *   5. Detail-sheet price face: cleaner tabular sans, heavier weight;
 *      serif reserved for the category heading only.
 *   6. Share PDF: "Include prices" toggle + permitted-tier subset.
 *      Server intersects the client's selection with the link's permitted
 *      set — never widens.
 *
 * Confidentiality invariants (unchanged):
 *   - Hidden tiers are ABSENT from the /catalog payload (server projection).
 *   - Price sorts refused with 400 when the link forbids the tier.
 *   - PDF byte-scan against FORBIDDEN_SUBSTRINGS under every toggle combo.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { formatMoney, formatWeight } from "@/lib/utils";
import { runSharePdfJob, shareErrorMessage } from "@/lib/sharePdfJob";
import {
  Loader2, Lock, X, Check, Download, Search, ImageOff, SlidersHorizontal,
  ArrowUpDown, Share2,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api`, timeout: 30000 });

/**
 * CS 21.74 — Customer-safe WhatsApp share for a single piece.
 *
 * Fetches the server-side share payload (single source of truth: caption,
 * product_page_url, image_url — all cost/B2B stripped by projection) and
 * hands it off to either:
 *   a) navigator.share({files:[...], text}) — mobile with Web Share API,
 *   b) https://wa.me/?text=… — desktop / unsupported fallback.
 *
 * The client NEVER assembles the caption from local B2B state, so a B2B
 * value loaded elsewhere on the page cannot leak into the outbound share.
 */
async function shareToWhatsApp(rlToken, password, jewelCode) {
  const params = new URLSearchParams({
    password,
    request_base: window.location.origin,
  });
  const { data } = await pub.get(
    `/wr/${rlToken}/product/${encodeURIComponent(jewelCode)}/share?${params}`,
  );
  const caption = data.caption || "";
  const imageUrl = data.image_url;
  // Try Web Share API with the image file (mobile).
  if (data.has_image && navigator.canShare && navigator.share) {
    try {
      const resp = await fetch(imageUrl, { credentials: "omit" });
      if (resp.ok) {
        const blob = await resp.blob();
        const file = new File([blob], `${jewelCode}.jpg`, { type: blob.type || "image/jpeg" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: caption });
          return;
        }
      }
    } catch (e) {
      // User cancelled the native share sheet → stop, do NOT relaunch WhatsApp.
      // Real failures (network, fetch, canShare) → fall through to wa.me.
      if (e && (e.name === "AbortError" || /abort|cancel/i.test(String(e.message || "")))) {
        return;
      }
    }
  }
  // Desktop / unsupported / no image → wa.me link (WhatsApp will unfurl OG).
  const url = `https://wa.me/?text=${encodeURIComponent(caption)}`;
  window.open(url, "_blank", "noopener");
}

const SESSION_PREFIX = "aps.wr.pw.";
const PRICE_VIS_KEY = "aps.wr.pxvis.";  // per-token price-visibility toggles

const SORT_OPTIONS_BASE = [
  { key: "gold_wt_desc",  label: "Gold weight — High to Low" },
  { key: "gold_wt_asc",   label: "Gold weight — Low to High" },
  { key: "dia_wt_desc",   label: "Diamond weight — High to Low" },
  { key: "dia_wt_asc",    label: "Diamond weight — Low to High" },
];
const SORT_OPTIONS_B2B = [
  { key: "price_b2b_desc", label: "B2B Price — High to Low" },
  { key: "price_b2b_asc",  label: "B2B Price — Low to High" },
];
const SORT_OPTIONS_MRP = [
  { key: "price_mrp_desc", label: "Suggested MRP — High to Low" },
  { key: "price_mrp_asc",  label: "Suggested MRP — Low to High" },
];

// ─── Password gate ──────────────────────────────────────────────────
function PasswordGate({ token, onOk }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!pw) { setErr("Enter the password"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await pub.post(`/wr/${token}/verify`, { password: pw });
      if (data?.ok) {
        sessionStorage.setItem(SESSION_PREFIX + token, pw);
        onOk(pw, data);
      }
    } catch (e) {
      const code = e.response?.status;
      if (code === 404) setErr("This link doesn't exist.");
      else if (code === 403) setErr("This link is no longer active.");
      else if (code === 401) setErr("Wrong password.");
      else setErr("Could not verify — try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#FAF8F3] flex items-center justify-center px-6" data-testid="wr-gate">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#C9A84C]/40 mb-6">
            <Lock className="w-4 h-4 text-[#C9A84C]" strokeWidth={1.5} />
          </div>
          <div className="font-serif text-[26px] tracking-tight text-[#0D1B2A] leading-tight">A private catalogue</div>
          <div className="text-[13px] text-slate-500 mt-2">Enter the password shared with you.</div>
        </div>
        <input
          autoFocus type="password"
          value={pw}
          onChange={(e) => { setPw(e.target.value); setErr(""); }}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Password"
          className="w-full text-center tracking-[0.15em] font-mono text-lg h-12 bg-transparent border-b border-[#0D1B2A]/25 focus:border-[#C9A84C] outline-none transition"
          data-testid="wr-gate-input"
        />
        {err && <p className="text-sm text-red-700/80 text-center mt-3" data-testid="wr-gate-error">{err}</p>}
        <button
          onClick={submit} disabled={busy}
          className="mt-8 w-full h-12 bg-[#0D1B2A] text-white font-medium text-sm tracking-[0.2em] uppercase hover:bg-[#0D1B2A]/85 transition disabled:opacity-50"
          data-testid="wr-gate-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Continue"}
        </button>
      </div>
    </div>
  );
}

// ─── Card ───────────────────────────────────────────────────────────
const PieceCard = React.memo(function PieceCard({ piece, currency, selected, onToggle, onOpen, canShare, showB2b, showMrp, rlToken, password }) {
  const img = piece.image;
  const specParts = [];
  if (piece.purity) specParts.push(piece.purity);
  if (Number(piece.gold_wt) > 0) specParts.push(`${formatWeight(piece.gold_wt)} g`);
  if (Number(piece.dia_total_ct) > 0) specParts.push(`${Number(piece.dia_total_ct).toFixed(2)} ct`);

  const hasB2B = showB2b && piece.b2b_price !== undefined && piece.b2b_price !== null;
  const hasMrp = showMrp && piece.suggested_mrp !== undefined && piece.suggested_mrp !== null;

  return (
    <div
      className="group relative cursor-pointer"
      data-testid={`wr-card-${piece.jewel_code}`}
      onClick={() => onOpen(piece)}
    >
        {/* Image frame — subtle 1px inner border on a warm ivory panel. */}
        <div className="relative aspect-square bg-[#F1EDE3] overflow-hidden ring-1 ring-[#0D1B2A]/10 rounded-[2px]">
          {img ? (
            <img
              src={img.startsWith("http") ? img : `${BACKEND}${img}`}
              alt={piece.product_code || piece.jewel_code}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageOff className="w-6 h-6 text-[#0D1B2A]/25" strokeWidth={1} />
            </div>
          )}

          {/* CS 21.42 / CS 21.45 -- Stock-status dot on the image (bottom-right).
              GREEN = in-stock (ready to ship / hand off).
              RED   = made-to-order design (priced through the same rate card,
                      not physically in hand yet).
              A subtle legend at the top of the grid tells the retailer what
              the dots mean. The dot is safe metadata: no cost / margin /
              bracket data attached; it just helps the retailer plan their
              order. */}
          {(piece.stock_status === "design" || piece.stock_status === "in_stock" || !piece.stock_status) && (
            <div
              className={`absolute bottom-2.5 right-2.5 w-3 h-3 rounded-full ring-2 ring-white shadow-sm ${
                piece.stock_status === "design" ? "bg-red-500" : "bg-emerald-500"
              }`}
              title={
                piece.stock_status === "design"
                  ? "Made to order — can be ordered, not ready in stock"
                  : "In stock — ready to ship"
              }
              data-testid={
                piece.stock_status === "design"
                  ? `wr-mto-${piece.jewel_code}`
                  : `wr-instock-${piece.jewel_code}`
              }
            />
          )}

        {/* CS 21.40 — top-left Checkbox. Always visible; tactile. */}
        {canShare && (
          <div
            className="absolute top-3 left-3 z-10"
            onClick={(e) => { e.stopPropagation(); onToggle(piece.jewel_code); }}
          >
            <div
              className={`w-6 h-6 rounded-[3px] border-2 flex items-center justify-center transition ${
                selected
                  ? "bg-[#0D1B2A] border-[#0D1B2A]"
                  : "bg-white/90 border-[#0D1B2A]/40 hover:border-[#0D1B2A]"
              }`}
              data-testid={`wr-select-${piece.jewel_code}`}
              role="checkbox"
              aria-checked={selected}
              aria-label={selected ? "Deselect" : "Select for share"}
            >
              {selected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
            </div>
          </div>
        )}

        {/* CS 21.74 — Per-card "Share on WhatsApp" (icon). Customer-safe:
            payload assembled server-side (no B2B in the caption). */}
        <button
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            try { await shareToWhatsApp(rlToken, password, piece.jewel_code); }
            catch { /* intentional: user cancelled the native share sheet or is offline -- not an error */ }
          }}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/95 hover:bg-white ring-1 ring-[#0D1B2A]/15 shadow-sm flex items-center justify-center transition"
          data-testid={`wr-card-wa-${piece.jewel_code}`}
          aria-label="Share on WhatsApp"
          title="Share on WhatsApp"
        >
          <Share2 className="w-3.5 h-3.5 text-[#0D1B2A]" strokeWidth={1.75} />
        </button>
      </div>

      {/* CS 21.40 — legible sans for code + spec line, elegant serif kept
          only for the category name. */}
      <div className="pt-4 space-y-1">
        <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-medium" data-testid={`wr-code-${piece.jewel_code}`}>
          {piece.product_code || piece.jewel_code}
        </div>
        <div className="font-serif text-[18px] text-[#0D1B2A] leading-snug">
          {piece.category || "Jewellery"}
        </div>
        <div className="text-[12px] text-slate-600 font-medium tabular-nums" data-testid={`wr-spec-${piece.jewel_code}`}>
          {specParts.join(" · ")}
        </div>

        {/* CS 21.40 — Prices ON the grid card. Only tiers the retailer
            has BOTH permitted AND toggled-on render. */}
        {(hasB2B || hasMrp) && (
          <div className="pt-2 space-y-0.5" data-testid={`wr-card-prices-${piece.jewel_code}`}>
            {hasB2B && (
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">B2B</span>
                <span className="text-[15px] font-semibold tabular-nums text-[#0D1B2A]" data-testid={`wr-card-price-b2b-${piece.jewel_code}`}>
                  {formatMoney(piece.b2b_price, currency)}
                </span>
              </div>
            )}
            {hasMrp && (
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">MRP</span>
                <span className="text-[15px] font-semibold tabular-nums text-[#0D1B2A]" data-testid={`wr-card-price-mrp-${piece.jewel_code}`}>
                  {formatMoney(piece.suggested_mrp, currency)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Detail sheet ────────────────────────────────────────────────────
function DetailSheet({ piece, currency, onClose, showB2b, showMrp, rlToken, password }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  if (!piece) return null;
  const img = piece.image;
  const shapeAgg = {};
  for (const ln of (piece.diamond_lines || [])) {
    const key = String(ln.shape || "Other");
    if (!shapeAgg[key]) shapeAgg[key] = { pcs: 0, ct: 0 };
    shapeAgg[key].pcs += Number(ln.pcs || 0);
    shapeAgg[key].ct += Number(ln.ct || 0);
  }
  const shapes = Object.entries(shapeAgg).sort((a, b) => b[1].ct - a[1].ct);
  // CS 21.41 — DetailSheet must respect the SAME effective visibility as
  // the grid. Two gates in series:
  //   1) Server projection (wholesaler permission) — hidden tier absent
  //      from payload; `piece.suggested_mrp` is undefined.
  //   2) Runtime retailer toggle (`showB2b` / `showMrp` props) — the retailer
  //      hid this tier on the grid; the detail sheet must stay in sync so
  //      the value doesn't leak here either.
  const hasB2B = !!showB2b && piece.b2b_price !== undefined && piece.b2b_price !== null;
  const hasMrp = !!showMrp && piece.suggested_mrp !== undefined && piece.suggested_mrp !== null;
  return (
    <div className="fixed inset-0 z-40 bg-[#0D1B2A]/40 backdrop-blur-sm flex items-stretch justify-end" data-testid="wr-detail">
      <div className="w-full sm:max-w-2xl h-full bg-[#FAF8F3] overflow-y-auto animate-in slide-in-from-right duration-300">
        <div className="sticky top-0 z-10 bg-[#FAF8F3]/95 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-[#0D1B2A]/8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-600 font-medium" data-testid="wr-detail-code">
            {piece.product_code || piece.jewel_code}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#0D1B2A]/5" data-testid="wr-detail-close" aria-label="Close">
            <X className="w-4 h-4 text-[#0D1B2A]" strokeWidth={1.5} />
          </button>
        </div>

        <div className="aspect-square bg-[#F1EDE3] w-full relative">
          {img ? (
            <img src={img.startsWith("http") ? img : `${BACKEND}${img}`}
                 alt={piece.product_code} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageOff className="w-8 h-8 text-[#0D1B2A]/20" strokeWidth={1} />
            </div>
          )}
          {/* CS 21.45 -- Status dot on the detail sheet, mirroring the grid.
              Green = in-stock, Red = made-to-order. Includes an inline label
              next to the dot so the meaning is unambiguous on the larger
              surface. */}
          {(piece.stock_status === "design" || piece.stock_status === "in_stock" || !piece.stock_status) && (
            <div
              className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-sm ring-1 ring-[#0D1B2A]/8"
              data-testid={
                piece.stock_status === "design"
                  ? `wr-detail-mto-${piece.jewel_code}`
                  : `wr-detail-instock-${piece.jewel_code}`
              }
            >
              <span className={`w-2.5 h-2.5 rounded-full ${piece.stock_status === "design" ? "bg-red-500" : "bg-emerald-500"}`} />
              <span className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#0D1B2A]">
                {piece.stock_status === "design" ? "Made to order" : "In stock"}
              </span>
            </div>
          )}
        </div>

        <div
          className="px-6 sm:px-10 py-10 space-y-10"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 7rem)" }}
        >
          <div>
            <div className="font-serif text-3xl text-[#0D1B2A] leading-tight" data-testid="wr-detail-name">
              {piece.category || "Jewellery"}
            </div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 mt-2 font-medium">
              {piece.jewel_code}
            </div>
          </div>

          {/* CS 21.40 — spec values in a cleaner tabular sans, heavier and
              higher contrast. */}
          <div className="space-y-3" data-testid="wr-detail-specs">
            <SpecRow label="Purity" value={piece.purity} />
            <SpecRow label="Gold weight" value={piece.gold_wt > 0 ? `${formatWeight(piece.gold_wt)} g` : "—"} />
            {piece.dia_total_pcs > 0 && (
              <SpecRow label="Diamonds" value={`${piece.dia_total_pcs} pcs · ${piece.dia_total_ct.toFixed(2)} ct`} />
            )}
          </div>

          {/* CS 21.52 -- Public per-piece description. The server only sends
              `description` on this payload when the wholesaler toggled
              "Show to customers" ON and the note is non-empty. Private
              notes are structurally impossible to reach this render. */}
          {piece.description && (
            <div
              className="rounded-md border border-[#0D1B2A]/8 bg-white/60 px-5 py-4"
              data-testid={`wr-detail-description-${piece.jewel_code}`}
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-medium">
                Note
              </div>
              <p className="text-[14px] leading-relaxed text-[#0D1B2A]/90 whitespace-pre-line">
                {piece.description}
              </p>
            </div>
          )}

          {shapes.length > 0 && (
            <div data-testid="wr-detail-shapes">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4 font-medium">
                Diamond breakup
              </div>
              <div className="border-t border-[#0D1B2A]/10">
                {shapes.map(([shape, agg]) => (
                  <div key={shape} className="flex items-center justify-between py-3 border-b border-[#0D1B2A]/8"
                       data-testid={`wr-shape-${shape}`}>
                    <div className="font-serif text-[17px] text-[#0D1B2A]">{shape}</div>
                    <div className="text-[14px] font-semibold tabular-nums text-[#0D1B2A]">
                      {agg.pcs} pcs <span className="text-slate-300 mx-2">·</span> {agg.ct.toFixed(3)} ct
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(hasB2B || hasMrp) && (
            <div className="pt-2" data-testid="wr-detail-prices">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4 font-medium">Price</div>
              <div className="space-y-4">
                {hasB2B && (
                  <div className="flex items-baseline justify-between gap-6">
                    <div className="text-[12px] tracking-[0.08em] text-slate-600 font-semibold uppercase">B2B Price</div>
                    {/* CS 21.41 — refined size: confident on a premium page,
                        never shouting. */}
                    <div className="text-[24px] font-semibold text-[#0D1B2A] tabular-nums leading-none tracking-tight" data-testid="wr-price-b2b">
                      {formatMoney(piece.b2b_price, currency)}
                    </div>
                  </div>
                )}
                {hasMrp && (
                  <div className="flex items-baseline justify-between gap-6">
                    <div className="text-[12px] tracking-[0.08em] text-slate-600 font-semibold uppercase">Suggested MRP</div>
                    <div className="text-[24px] font-semibold text-[#0D1B2A] tabular-nums leading-none tracking-tight" data-testid="wr-price-mrp">
                      {formatMoney(piece.suggested_mrp, currency)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CS 21.74 — Prominent "Share on WhatsApp" CTA. Uses the
              server-minted share payload (customer-safe: MRP-only when
              show_mrp; never B2B). */}
          <div className="pt-2">
            <button
              type="button"
              onClick={async () => {
                try { await shareToWhatsApp(rlToken, password, piece.jewel_code); }
                catch { /* intentional: user cancelled the native share sheet or is offline -- not an error */ }
              }}
              className="w-full h-12 rounded-md bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold tracking-wide flex items-center justify-center gap-2 transition"
              data-testid={`wr-detail-wa-${piece.jewel_code}`}
              aria-label="Share on WhatsApp"
            >
              <Share2 className="w-4 h-4" strokeWidth={2} />
              Share on WhatsApp
            </button>
            <div className="mt-2 text-[11px] text-slate-500 text-center">
              Customer view — {showMrp && piece.suggested_mrp !== undefined ? "photo, specs & suggested price" : "photo & specs, no price"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-medium">{label}</div>
      <div className="text-[15px] font-semibold text-[#0D1B2A] tabular-nums">{value}</div>
    </div>
  );
}

// ─── Filter Chip / Panel ─────────────────────────────────────────────
function Chip({ children, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-[12px] tracking-wide rounded-full border transition font-medium ${
        active
          ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
          : "bg-transparent text-[#0D1B2A]/75 border-[#0D1B2A]/25 hover:border-[#0D1B2A]/60"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function FilterPanel({ categories, shapes, catSel, shapeSel, toggleCat, toggleShape, clearAll }) {
  return (
    <div className="space-y-6" data-testid="wr-filter-panel">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3 font-medium">Category</div>
        <div className="flex flex-wrap gap-2">
          {categories.length === 0 && <span className="text-xs text-slate-400">No categories.</span>}
          {categories.map((c) => (
            <Chip key={c} active={catSel.has(c)} onClick={() => toggleCat(c)} testid={`wr-filter-cat-${c}`}>{c}</Chip>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3 font-medium">Diamond shape</div>
        <div className="flex flex-wrap gap-2">
          {shapes.length === 0 && <span className="text-xs text-slate-400">No diamond pieces in this catalogue.</span>}
          {shapes.map((s) => (
            <Chip key={s} active={shapeSel.has(s)} onClick={() => toggleShape(s)} testid={`wr-filter-shape-${s}`}>{s}</Chip>
          ))}
        </div>
      </div>
      {(catSel.size > 0 || shapeSel.size > 0) && (
        <button
          onClick={clearAll}
          className="text-[11px] tracking-wide text-slate-500 hover:text-[#0D1B2A] underline underline-offset-4"
          data-testid="wr-filter-clear"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}

// ─── Share Dialog (with "Include prices" toggle + subset picker) ─────
function ShareDialog({ open, onOpenChange, token, password, selected, meta, onDone }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const permittedB2B = !!meta?.show_b2b && !!meta?.prices_locked;
  const permittedMrp = !!meta?.show_mrp && !!meta?.prices_locked;
  const anyPermitted = permittedB2B || permittedMrp;
  const [withPrice, setWithPrice] = useState(anyPermitted);
  const [pickB2B, setPickB2B] = useState(permittedB2B);
  const [pickMrp, setPickMrp] = useState(permittedMrp);
  useEffect(() => {
    if (!open) return;
    // Reset on open — respect current link permissions.
    setWithPrice(anyPermitted);
    setPickB2B(permittedB2B);
    setPickMrp(permittedMrp);
    setError("");
    setProgress(null);
  }, [open, anyPermitted, permittedB2B, permittedMrp]);

  const submit = async () => {
    if (selected.length === 0 || busy) return;
    if (withPrice && !pickB2B && !pickMrp) return;
    setBusy(true); setError("");
    try {
      const tiers = [];
      if (pickB2B) tiers.push("b2b");
      if (pickMrp) tiers.push("customer");
      const { blob: raw } = await runSharePdfJob({
        client: pub, base: `/wr/${token}`,
        createBody: { password, jewel_codes: selected, with_price: withPrice, selected_tiers: withPrice ? tiers : null },
        authBody: { password },
        onProgress: (done, total) => setProgress({ done, total }),
      });
      const blob = new Blob([raw], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(meta?.retailer_label || "catalogue").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      setError(await shareErrorMessage(e));
    } finally { setBusy(false); setProgress(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FAF8F3] max-w-md" data-testid="wr-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0D1B2A]">Download PDF</DialogTitle>
          <p className="text-[13px] text-slate-500 mt-1" data-testid="wr-share-count">{selected.length} piece{selected.length === 1 ? "" : "s"} selected.</p>
        </DialogHeader>
        <div className="space-y-5 py-4">
          {anyPermitted && (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-[#0D1B2A]">Include prices</div>
                <div className="text-[11px] text-slate-500">Off → photo + specs only. On → the tier(s) below.</div>
              </div>
              <Switch checked={withPrice} onCheckedChange={setWithPrice} data-testid="wr-share-with-price" />
            </div>
          )}
          {anyPermitted && withPrice && (
            <div className="pl-1 space-y-3 border-l-2 border-[#C9A84C]/40">
              <div className="pl-4 space-y-3">
                {permittedB2B && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={pickB2B} onCheckedChange={setPickB2B} data-testid="wr-share-pick-b2b" />
                    <span className="text-[13px]">B2B Price</span>
                  </label>
                )}
                {permittedMrp && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={pickMrp} onCheckedChange={setPickMrp} data-testid="wr-share-pick-mrp" />
                    <span className="text-[13px]">Suggested MRP</span>
                  </label>
                )}
                {withPrice && !pickB2B && !pickMrp && (
                  <p className="text-[11px] text-red-700/80">Pick at least one tier or switch Include prices off.</p>
                )}
              </div>
            </div>
          )}
          {!anyPermitted && (
            <p className="text-[13px] text-slate-500">This link is specs-only. The PDF will contain photos and specs.</p>
          )}
          {busy && progress && (
            <div className="space-y-1.5" data-testid="wr-share-progress">
              <div className="text-[12px] text-[#0D1B2A]">Preparing PDF, {progress.done} of {progress.total}</div>
              <div className="h-1.5 bg-[#0D1B2A]/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#C9A84C] transition-[width] duration-300" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}
          {error && <p className="text-[12px] text-red-700" data-testid="wr-share-error">{error}</p>}
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="text-[13px] text-slate-500 hover:text-[#0D1B2A] px-4 py-2 disabled:opacity-50"
            data-testid="wr-share-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy || selected.length === 0 || (withPrice && anyPermitted && !pickB2B && !pickMrp)}
            className="bg-[#0D1B2A] text-white text-[13px] font-medium tracking-wide px-5 py-2.5 rounded-full flex items-center gap-1.5 disabled:opacity-50"
            data-testid="wr-share-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main ──────────────────────────────────────────────────────────
export default function RetailerShopfront() {
  const { token } = useParams();
  const [password, setPassword] = useState(() => sessionStorage.getItem(SESSION_PREFIX + token) || "");
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gateErr, setGateErr] = useState(false);
  const [search, setSearch] = useState("");
  const [activePiece, setActivePiece] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [shareOpen, setShareOpen] = useState(false);
  const [sort, setSort] = useState("");
  const [catSel, setCatSel] = useState(new Set());
  const [shapeSel, setShapeSel] = useState(new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  // CS 21.40 — grid price visibility. Bounded by link permissions; a hidden
  // tier can NEVER be toggled on. Default = on for whatever the link permits.
  const [pxVis, setPxVis] = useState(() => {
    try {
      const raw = sessionStorage.getItem(PRICE_VIS_KEY + token);
      if (raw) return { b2b: true, mrp: true, ...JSON.parse(raw) };
    } catch (err) {
      // Safari private mode / corrupt JSON — fall through to default.
      console.warn("[wr] pxVis read failed; using defaults.", err);
    }
    return { b2b: true, mrp: true };
  });
  useEffect(() => {
    try { sessionStorage.setItem(PRICE_VIS_KEY + token, JSON.stringify(pxVis)); }
    catch (err) {
      // Safari private mode / quota — non-blocking; toggle just won't persist.
      console.warn("[wr] pxVis write failed; state kept in memory.", err);
    }
  }, [pxVis, token]);

  const canShare = !!meta && (meta.show_b2b || meta.show_mrp || true);  // share works even for specs-only
  // Effective per-tier visibility on the grid: permitted × user toggle.
  const showB2b = !!(meta?.show_b2b && meta?.prices_locked && pxVis.b2b);
  const showMrp = !!(meta?.show_mrp && meta?.prices_locked && pxVis.mrp);

  const load = useCallback(async (pw, sortKey) => {
    setLoading(true); setGateErr(false);
    try {
      const params = { password: pw };
      if (sortKey) params.sort = sortKey;
      const { data } = await pub.get(`/wr/${token}/catalog`, { params });
      setMeta({
        wholesaler_name: data.wholesaler_name,
        retailer_label: data.retailer_label,
        currency: data.currency,
        show_b2b: data.show_b2b,
        show_mrp: data.show_mrp,
        prices_locked: data.prices_locked,
      });
      setItems(data.items || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 400 && sortKey) {
        setSort("");
        try {
          const { data } = await pub.get(`/wr/${token}/catalog`, { params: { password: pw } });
          setMeta({
            wholesaler_name: data.wholesaler_name,
            retailer_label: data.retailer_label,
            currency: data.currency,
            show_b2b: data.show_b2b,
            show_mrp: data.show_mrp,
            prices_locked: data.prices_locked,
          });
          setItems(data.items || []);
        } catch (err) {
          // Fallback fetch also failed — swallow and bounce to gate below.
          console.warn("[wr] catalog fallback fetch failed.", err);
        }
      } else {
        sessionStorage.removeItem(SESSION_PREFIX + token);
        setPassword("");
        setGateErr(true);
      }
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (password) load(password, sort);
  }, [password, sort, load]);

  // If the wholesaler has now hidden a tier we had toggled on, quietly
  // drop it — a hidden tier CAN'T be toggled on.
  useEffect(() => {
    if (!meta) return;
    setPxVis((prev) => ({
      b2b: prev.b2b && !!meta.show_b2b,
      mrp: prev.mrp && !!meta.show_mrp,
    }));
  }, [meta]);

  const categoryOptions = useMemo(
    () => Array.from(new Set(items.map((it) => it.category).filter(Boolean))).sort(),
    [items]);
  const shapeOptions = useMemo(() => {
    const s = new Set();
    for (const it of items) for (const ln of (it.diamond_lines || [])) if (ln.shape) s.add(String(ln.shape));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    let out = items;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((it) =>
        String(it.jewel_code || "").toLowerCase().includes(q) ||
        String(it.product_code || "").toLowerCase().includes(q) ||
        String(it.category || "").toLowerCase().includes(q)
      );
    }
    if (catSel.size > 0) out = out.filter((it) => catSel.has(it.category));
    if (shapeSel.size > 0) {
      out = out.filter((it) =>
        (it.diamond_lines || []).some((ln) => shapeSel.has(String(ln.shape)))
      );
    }
    return out;
  }, [items, search, catSel, shapeSel]);

  const availableSorts = useMemo(() => {
    if (!meta) return SORT_OPTIONS_BASE;
    const out = [];
    if (meta.show_b2b && meta.prices_locked) out.push(...SORT_OPTIONS_B2B);
    if (meta.show_mrp && meta.prices_locked) out.push(...SORT_OPTIONS_MRP);
    out.push(...SORT_OPTIONS_BASE);
    return out;
  }, [meta]);

  useEffect(() => {
    if (!sort || !meta) return;
    const ok = availableSorts.some((o) => o.key === sort);
    if (!ok) setSort("");
  }, [sort, meta, availableSorts]);

  const toggleSelect = useCallback((code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);
  const toggleCat = useCallback((c) => setCatSel((prev) => {
    const next = new Set(prev); if (next.has(c)) next.delete(c); else next.add(c); return next;
  }), []);
  const toggleShape = useCallback((s) => setShapeSel((prev) => {
    const next = new Set(prev); if (next.has(s)) next.delete(s); else next.add(s); return next;
  }), []);
  const filteredCodes = useMemo(() => filtered.map((it) => it.jewel_code), [filtered]);
  const allFilteredSelected = filteredCodes.length > 0 && filteredCodes.every((c) => selected.has(c));
  const selectedOutsideFilter = useMemo(() => {
    const inView = new Set(filteredCodes);
    let n = 0;
    selected.forEach((c) => { if (!inView.has(c)) n += 1; });
    return n;
  }, [selected, filteredCodes]);
  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (allFilteredSelected) return new Set();
      const next = new Set(prev);
      filteredCodes.forEach((c) => next.add(c));
      return next;
    });
  }, [allFilteredSelected, filteredCodes]);
  const selectionLabel = selectedOutsideFilter === 0
    ? `${selected.size} of ${filteredCodes.length} selected`
    : `${selected.size} selected · ${filteredCodes.length} matching`;

  useEffect(() => {
    document.title = meta?.wholesaler_name || "Private catalogue";
    document.querySelectorAll("link[rel='icon'],link[rel='apple-touch-icon']").forEach((l) => {
      l.href = "/brand/neutral-icon.svg"; l.type = "image/svg+xml";
    });
  }, [meta?.wholesaler_name]);

  const clearAllFilters = useCallback(() => { setCatSel(new Set()); setShapeSel(new Set()); }, []);
  const activeFilterCount = catSel.size + shapeSel.size;

  if (!password || gateErr) {
    return <PasswordGate token={token} onOk={(pw) => { setPassword(pw); setGateErr(false); }} />;
  }

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="wr-shop">
      {/* CS 22.11 — Wholesaler name is the brand on wholesaler-created
          retailer links (no Ashth logo/name). */}
      <header className="bg-[#FAF8F3] border-b border-[#0D1B2A]/8">
        <div className="max-w-[1400px] mx-auto px-6 pt-12 pb-6 text-center" data-testid="wr-brand">
          <div className="mx-auto w-10 h-px bg-[#C9A84C] mb-5" />
          <div className="font-serif text-[34px] sm:text-[44px] text-[#0D1B2A] leading-tight tracking-tight" data-testid="wr-shop-name">
            {meta?.wholesaler_name || "Catalogue"}
          </div>
          {meta?.retailer_label && (
            <div className="text-[11px] tracking-[0.28em] uppercase text-slate-500 mt-2 font-medium" data-testid="wr-retailer-label">
              for {meta.retailer_label}
            </div>
          )}
          {/* Large central search */}
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="relative">
              <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
              <input
                type="text" value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search jewel code, style, or category"
                className="w-full h-14 pl-12 pr-4 bg-white/60 border border-[#0D1B2A]/12 rounded-full focus:border-[#C9A84C] focus:bg-white outline-none text-[15px] font-medium placeholder:text-slate-400 placeholder:font-normal transition"
                data-testid="wr-search"
              />
            </div>
          </div>
        </div>

      </header>

      {/* Toolbar — sort + filter + price toggles + select all. Sticky on
          scroll (CS 22.11: lives outside <header> so it stays pinned). */}
        <div className="sticky top-0 z-20 bg-[#FAF8F3]/95 backdrop-blur border-t border-[#0D1B2A]/6">
          <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-3 flex-wrap" data-testid="wr-toolbar">
            <div className="w-full sm:w-auto flex items-center gap-3 sm:order-last sm:pl-3 sm:border-l sm:border-[#0D1B2A]/10" data-testid="wr-select-row">
              <span className="text-[12px] font-medium text-[#0D1B2A] tabular-nums" data-testid="wr-select-counter">{selectionLabel}</span>
              <button
                onClick={toggleSelectAll}
                disabled={filteredCodes.length === 0}
                className="ml-auto sm:ml-0 h-8 px-3.5 rounded-full border border-[#0D1B2A] text-[12px] font-semibold tracking-wide text-[#0D1B2A] hover:bg-[#0D1B2A] hover:text-white transition-colors disabled:opacity-40"
                data-testid="wr-select-all"
              >
                {allFilteredSelected ? "Clear selection" : "Select all"}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-3.5 h-3.5 text-[#0D1B2A]/50" strokeWidth={1.5} />
              <Select value={sort || "none"} onValueChange={(v) => setSort(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 min-w-[220px] bg-transparent border-[#0D1B2A]/15 text-[13px] font-medium" data-testid="wr-sort-trigger">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="wr-sort-none">Default order</SelectItem>
                  {availableSorts.map((opt) => (
                    <SelectItem key={opt.key} value={opt.key} data-testid={`wr-sort-${opt.key}`}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* CS 21.40 — Retailer price toggle. ONLY tiers the wholesaler
                permitted render — no hidden tier is even offered. */}
            {(meta?.show_b2b || meta?.show_mrp) && meta?.prices_locked && (
              <div className="flex items-center gap-4 pl-2 border-l border-[#0D1B2A]/10" data-testid="wr-price-toggles">
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium hidden sm:inline">Show</span>
                {meta.show_b2b && (
                  <label className="flex items-center gap-1.5 text-[12px] font-medium cursor-pointer" data-testid="wr-toggle-b2b-label">
                    <Switch checked={pxVis.b2b} onCheckedChange={(v) => setPxVis((p) => ({ ...p, b2b: v }))} data-testid="wr-toggle-b2b" />
                    B2B
                  </label>
                )}
                {meta.show_mrp && (
                  <label className="flex items-center gap-1.5 text-[12px] font-medium cursor-pointer" data-testid="wr-toggle-mrp-label">
                    <Switch checked={pxVis.mrp} onCheckedChange={(v) => setPxVis((p) => ({ ...p, mrp: v }))} data-testid="wr-toggle-mrp" />
                    MRP
                  </label>
                )}
              </div>
            )}

            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <button
                  className={`ml-auto h-9 px-3 rounded-full border text-[13px] font-medium tracking-wide flex items-center gap-2 transition ${
                    activeFilterCount > 0
                      ? "border-[#0D1B2A] bg-[#0D1B2A]/5 text-[#0D1B2A]"
                      : "border-[#0D1B2A]/15 text-[#0D1B2A]/70 hover:border-[#0D1B2A]/40"
                  }`}
                  data-testid="wr-filter-open"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="text-[10px] rounded-full bg-[#0D1B2A] text-white px-1.5 py-0.5 leading-none" data-testid="wr-filter-count">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#FAF8F3] w-full sm:max-w-md overflow-y-auto" data-testid="wr-filter-sheet">
                <SheetHeader className="text-left mb-6">
                  <SheetTitle className="font-serif text-2xl text-[#0D1B2A]">Filters</SheetTitle>
                </SheetHeader>
                <FilterPanel
                  categories={categoryOptions} shapes={shapeOptions}
                  catSel={catSel} shapeSel={shapeSel}
                  toggleCat={toggleCat} toggleShape={toggleShape}
                  clearAll={clearAllFilters}
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>

      {loading && (
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="max-w-[1400px] mx-auto px-6 py-32 text-center">
          <div className="font-serif text-xl text-[#0D1B2A]/70" data-testid="wr-empty">
            {items.length === 0 ? "This catalogue is currently empty." : "No matches for your filters."}
          </div>
        </div>
      )}

      {/* CS 21.40 — 1 / 2 / 3 columns. More premium presentation. */}
      {!loading && filtered.length > 0 && (
        <main className="max-w-[1400px] mx-auto px-6 py-10 pb-32" data-testid="wr-grid">
          {/* CS 21.45 -- Legend explaining the status dots on each card.
              Kept subtle so it doesn't crowd the premium grid, but always
              on-screen when there's anything to show. */}
          <div
            className="flex items-center justify-center gap-6 text-[11px] tracking-[0.15em] uppercase text-slate-500 font-medium mb-8"
            data-testid="wr-status-legend"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white shadow-sm" />
              In stock
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 ring-2 ring-white shadow-sm" />
              Made to order
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
            {filtered.map((piece) => (
              <PieceCard
                key={piece.jewel_code}
                piece={piece}
                currency={meta?.currency || "USD"}
                selected={selected.has(piece.jewel_code)}
                onToggle={toggleSelect}
                onOpen={setActivePiece}
                canShare={canShare}
                showB2b={showB2b}
                showMrp={showMrp}
                rlToken={token}
                password={password}
              />
            ))}
          </div>
        </main>
      )}

      {selected.size > 0 && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-30 bg-[#0D1B2A] text-white rounded-full shadow-2xl flex items-center gap-1 pl-5 pr-1.5 py-1.5"
          style={{ bottom: "max(env(safe-area-inset-bottom), 1.5rem)" }}
          data-testid="wr-selection-bar"
        >
          <span className="text-[13px] tracking-wide font-medium whitespace-nowrap tabular-nums" data-testid="wr-selection-count">
            {selectionLabel}
          </span>
          <button
            onClick={() => setSelected(new Set())}
            className="text-[11px] text-white/60 hover:text-white px-2.5 py-1"
            data-testid="wr-selection-clear"
          >clear</button>
          <button
            onClick={() => setShareOpen(true)}
            className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-[#0D1B2A] text-[13px] font-semibold pl-3.5 pr-4 py-2 rounded-full flex items-center gap-1.5"
            data-testid="wr-share-download"
          >
            <Download className="w-3.5 h-3.5" />
            PDF
          </button>
        </div>
      )}

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        token={token}
        password={password}
        selected={Array.from(selected)}
        meta={meta}
        onDone={() => setSelected(new Set())}
      />

      <DetailSheet
        piece={activePiece}
        currency={meta?.currency || "USD"}
        onClose={() => setActivePiece(null)}
        showB2b={showB2b}
        showMrp={showMrp}
        rlToken={token}
        password={password}
      />
    </div>
  );
}
